// ProvablyFairPage.jsx
import React, {
  Fragment,
  useMemo,
  useState
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";

import {
  ArrowRight,
  BadgeCheck,
  Binary,
  Blocks,
  Braces,
  Check,
  ChevronDown,
  CircleDot,
  Copy,
  Database,
  Eye,
  FileCheck2,
  Fingerprint,
  Gamepad2,
  GitBranch,
  Hash,
  KeyRound,
  Layers3,
  Link2,
  LockKeyhole,
  Network,
  Orbit,
  RefreshCw,
  Scale,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  SquareTerminal,
  TimerReset,
  TriangleAlert,
  Workflow,
  X
} from "lucide-react";

import "./Styles.css";
import HeaderToMainPage from "../Universal/Header/Index";
import Footer from "../Universal/Footer/Index";
import { Link } from "react-router-dom";


/* ============================================================
   CONTENT
============================================================ */

const verificationStages = [
  {
    id: "commit",
    index: "01",
    kicker: "BEFORE RANDOM RESOLUTION",
    title: "Commit",
    icon: Fingerprint,
    accent: "cyan",
    headline: "Establish the random input before play.",
    body:
      "For supported seeded games, Ethosino commits to hidden server-side seed material before the relevant outcome. The public hash anchors that input without disclosing the active secret, making later substitution detectable after reveal.",
    evidence: [
      "Active server seed is not exposed to the player",
      "Commitment precedes the applicable random result",
      "Seed lifecycle is associated with its tenant"
    ],
    telemetry: [
      ["STATE", "COMMITTED"],
      ["SECRET", "HIDDEN"],
      ["TIMING", "PRE-DRAW"]
    ]
  },
  {
    id: "resolve",
    index: "02",
    kicker: "WHEN RANDOMNESS IS USED",
    title: "Resolve",
    icon: Binary,
    accent: "violet",
    headline: "Apply the correct method for the game.",
    body:
      "A supported numeric game may map deterministic bytes to a number; a card game may derive a shuffled deck. The client seed, nonce and versioned rules explain how the input stream became that result. Skill-driven decisions are examined through recorded actions instead.",
    evidence: [
      "Seeded games use recorded client input",
      "Nonce identifies the sequence of random draws",
      "Game-specific mapping logic is versioned"
    ],
    telemetry: [
      ["INPUTS", "RECORDED"],
      ["NONCE", "MONOTONIC"],
      ["RULESET", "VERSIONED"]
    ]
  },
  {
    id: "reveal",
    index: "03",
    kicker: "AFTER THE SEED WINDOW",
    title: "Reveal",
    icon: KeyRound,
    accent: "green",
    headline: "Reveal the completed seed window.",
    body:
      "Following seed rotation, the previous secret can be disclosed for applicable games. Rehashing that material against the earlier seed commitment tests whether the original random input was retained; it does not, by itself, validate every game rule or decision.",
    evidence: [
      "Reveal follows retirement of the active seed",
      "Rehashed secret is compared with the commitment",
      "Prior seed windows remain attributable"
    ],
    telemetry: [
      ["WINDOW", "CLOSED"],
      ["REVEAL", "AVAILABLE"],
      ["HASH", "SAMPLE MATCH"]
    ]
  },
  {
    id: "reproduce",
    index: "04",
    kicker: "DURING AUDIT REVIEW",
    title: "Reproduce",
    icon: RefreshCw,
    accent: "pink",
    headline: "Challenge the saved result.",
    body:
      "The auditor reruns the supported calculation with the historic inputs and rules. For stateful or skill-based games, the sandbox reconstructs the available sequence of actions and state changes. Differences are reviewable instead of hidden behind a success badge.",
    evidence: [
      "Supported random outcomes can be recomputed",
      "Historical game logic is needed for valid replay",
      "Differences are surfaced for investigation"
    ],
    telemetry: [
      ["REPLAY", "HISTORICAL"],
      ["COMPARE", "OUTCOMES"],
      ["STATUS", "REVIEWABLE"]
    ]
  }
];


const proofArtifacts = [
  {
    id: "commitment",
    icon: Hash,
    label: "SEED COMMIT",
    title: "Pre-round commitment reference",
    value: "9f7b2a…c4d1",
    description:
      "The previously published hash against which a retired server seed can be checked."
  },
  {
    id: "client",
    icon: Fingerprint,
    label: "CLIENT INPUT",
    title: "The player-side input",
    value: "player-session-08f1",
    description:
      "A recorded client seed used in supported deterministic random calculations."
  },
  {
    id: "nonce",
    icon: Binary,
    label: "SEQUENCE",
    title: "Which draw occurred",
    value: "1842",
    description:
      "The sequence value distinguishes successive draws within a seeded game session."
  },
  {
    id: "ruleset",
    icon: GitBranch,
    label: "RULESET",
    title: "Rules for this game version",
    value: "mines:v3.2.0",
    description:
      "Preserves how random bytes, shuffled decks or recorded actions were interpreted at the time."
  },
  {
    id: "reveal",
    icon: KeyRound,
    label: "REVEAL",
    title: "Retired seed disclosure",
    value: "available after rotation",
    description:
      "Allows the retired seed to be checked without exposing active random material."
  },
  {
    id: "result",
    icon: BadgeCheck,
    label: "REPLAY RESULT",
    title: "Audit comparison status",
    value: "MATCH",
    description:
      "Illustrates agreement between a sandbox result and its stored counterpart; example values are not live proof."
  }
];


const lifecycleSteps = [
  {
    index: "01",
    title: "Define",
    icon: LockKeyhole,
    body:
      "Specify the game category, allowed interactions and what constitutes a valid outcome."
  },
  {
    index: "02",
    title: "Version",
    icon: Hash,
    body:
      "Record the version of the rules, outcome mapping and game-specific verification method."
  },
  {
    index: "03",
    title: "Capture",
    icon: Binary,
    body:
      "Capture the relevant random inputs, actions, state transitions and result references."
  },
  {
    index: "04",
    title: "Protect",
    icon: TimerReset,
    body:
      "Keep active secrets inaccessible while retaining historical records for later examination."
  },
  {
    index: "05",
    title: "Reconstruct",
    icon: KeyRound,
    body:
      "Enable a sandbox reconstruction under the rules that originally governed the session."
  },
  {
    index: "06",
    title: "Review",
    icon: FileCheck2,
    body:
      "Investigate discrepancies and preserve a clear record of the auditor’s conclusion."
  }
];


const operatorOutcomes = [
  {
    icon: Scale,
    label: "CARD & DECK GAMES",
    title: "Audit the order of the deck.",
    body:
      "For supported card games, replay the original shuffle and subsequent draws against the game’s recorded rules. The audit must distinguish the deck order from player decisions made during the hand."
  },
  {
    icon: ShieldCheck,
    label: "NUMERICAL OUTCOMES",
    title: "Trace a number to its source.",
    body:
      "Dice, wheel positions, number selections and similar results require a documented conversion from the random input stream to a valid value. Replay checks the actual mapping, not merely a displayed number."
  },
  {
    icon: Network,
    label: "RNG-DRIVEN GAMES",
    title: "Verify randomness at the source.",
    body:
      "For supported seeded RNG games, compare the published commitment, revealed material, client input, nonce and ruleset. Repeated draws remain identifiable within the correct game and tenant context."
  },
  {
    icon: Blocks,
    label: "SKILL & EVENT GAMES",
    title: "Reconstruct actions, not just luck.",
    body:
      "Skill outcomes depend on input timing, recorded decisions and game state. Per-game sandboxes replay the available event history to inspect rule enforcement, without pretending a seed hash can prove the quality of player skill."
  }
];


const boundaryCards = [
  {
    type: "yes",
    icon: Check,
    eyebrow: "WHAT EVIDENCE CAN SUPPORT",
    title: "Defined claims can be tested.",
    items: [
      "A revealed seed corresponds to its earlier commitment where that model applies.",
      "A supported result can be recomputed under historical game logic.",
      "Recorded decks, actions and game-state transitions can be inspected in their replay context.",
      "Differences between reconstructed and saved events can be identified for review."
    ]
  },
  {
    type: "no",
    icon: X,
    eyebrow: "WHAT THE EVIDENCE CANNOT GUARANTEE",
    title: "Fairness has multiple dimensions.",
    items: [
      "Verifiable randomness does not make odds or payouts generous.",
      "A recorded skill-game replay does not prove the absence of every form of cheating.",
      "A matched replay does not establish that the rules themselves are ethical.",
      "None of these mechanisms replaces independent testing, licensing or operator duties."
    ]
  }
];


const faqItems = [
  {
    q: "How are odds, RTP and provably-fair verification different?",
    a:
      "RTP and pay tables describe game economics; they do not prove that an individual round followed the published mechanics. Cryptographic verification tests a particular supported random outcome. Ethical game design must address both accurate disclosures and integrity of execution."
  },
  {
    q: "How can a historical deck order be examined?",
    a:
      "For supported seeded card games, the recorded inputs and historical shuffle logic can reconstruct the deck order. The sandbox then helps trace deals, draws and player decisions in sequence. A deck replay evaluates the recorded mechanism; it is not automatically a certification of the entire card game."
  },
  {
    q: "How are numerical draws and RNG outcomes evaluated?",
    a:
      "A reproducible random stream is only the starting point. The game-specific converter must map it correctly into dice values, mine positions, wheel segments or another permitted result. We preserve the applicable rule version so an updated game does not rewrite the interpretation of an old draw."
  },
  {
    q: "Can a skill-based game be provably fair in the same way?",
    a:
      "Not in exactly the same way. A seed commitment can help verify supported random elements, but skilled play must also be examined through the available input sequence, timing, state transitions and rule enforcement. A sandbox event replay supports that investigation; it is not a blanket cryptographic proof of skill or anti-cheat effectiveness."
  },
  {
    q: "Does a sandbox replay modify a completed game or wallet?",
    a:
      "The fairness sandbox is intended for historical inspection rather than live gameplay or settlement. Replay should reconstruct from preserved inputs without reapplying financial consequences. For a production audit, confirm the game’s replay coverage and the controls separating it from live transactions."
  },
  {
    q: "What about third-party games and external RNG providers?",
    a:
      "Their provider determines the underlying RNG, certification and verification approach. Ethosino may retain surrounding platform and transaction evidence, but cannot extend first-party seed or state-replay guarantees to externally controlled outcomes without the provider’s own supporting artifacts."
  },
  {
    q: "What does an ethical fairness policy require beyond replay?",
    a:
      "Clear rules and payout disclosures, responsible-gaming controls, access restrictions, documented changes, a meaningful dispute process and independent review where required. Reproducibility is one strong control, not a complete ethical or regulatory framework."
  }
];


/* ============================================================
   MOTION
============================================================ */

const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0
}) => {
  const reduceMotion = useReducedMotion();

  const starts = {
    left: {
      x: -115,
      y: 0,
      rotate: -1.5
    },
    right: {
      x: 115,
      y: 0,
      rotate: 1.5
    },
    up: {
      x: 0,
      y: 72,
      rotate: 0
    },
    topLeft: {
      x: -145,
      y: -95,
      rotate: -2.4
    },
    topRight: {
      x: 145,
      y: -95,
      rotate: 2.4
    },
    bottomLeft: {
      x: -145,
      y: 100,
      rotate: -2.4
    },
    bottomRight: {
      x: 145,
      y: 100,
      rotate: 2.4
    }
  };

  const start =
    starts[direction] ||
    starts.up;

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              ...start,
              scale: 0.955,
              filter: "blur(10px)"
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
        filter: "blur(0px)"
      }}
      viewport={{
        once: true,
        amount: 0.14
      }}
      transition={{
        duration: reduceMotion
          ? 0
          : 0.82,
        delay,
        ease: [
          0.16,
          1,
          0.3,
          1
        ]
      }}
    >
      {children}
    </motion.div>
  );
};


const Spotlight = ({
  children,
  className = ""
}) => {
  const handleMove = event => {
    const rect =
      event.currentTarget.getBoundingClientRect();

    event.currentTarget.style.setProperty(
      "--pf8-mx",
      `${event.clientX - rect.left}px`
    );

    event.currentTarget.style.setProperty(
      "--pf8-my",
      `${event.clientY - rect.top}px`
    );
  };

  return (
    <div
      className={`eth-pf-v8-spotlight ${className}`}
      onPointerMove={handleMove}
    >
      <div className="eth-pf-v8-spotlight-glow" />
      {children}
    </div>
  );
};


/* ============================================================
   PROOF REACTOR
============================================================ */

const ProofReactor = ({
  reduceMotion
}) => {
  return (
    <div className="eth-pf-v8-reactor">
      <div className="eth-pf-v8-reactor-grid" />
      <div className="eth-pf-v8-reactor-scan" />

      <div className="eth-pf-v8-reactor-top">
        <div>
          <span className="eth-pf-v8-live-dot" />
          ILLUSTRATIVE PROOF PATH
        </div>

        <b>
          FIRST_PARTY / EXAMPLE
        </b>
      </div>


      <div className="eth-pf-v8-reactor-field">

        <motion.div
          className="eth-pf-v8-ring eth-pf-v8-ring--outer"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate: 360
                }
          }
          transition={{
            duration: 32,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="eth-pf-v8-ring eth-pf-v8-ring--middle"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate: -360
                }
          }
          transition={{
            duration: 23,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="eth-pf-v8-ring eth-pf-v8-ring--inner"
          animate={
            reduceMotion
              ? {}
              : {
                  rotate: 360
                }
          }
          transition={{
            duration: 17,
            repeat: Infinity,
            ease: "linear"
          }}
        />


        <div className="eth-pf-v8-reactor-core">
          <span className="eth-pf-v8-reactor-wave" />

          <Fingerprint
            size={29}
            strokeWidth={1.6}
          />

          <small>
            ETHOSINO
          </small>

          <strong>
            AUDIT CORE
          </strong>

          <b>
            GAME-SPECIFIC
          </b>
        </div>


        {[
          {
            className:
              "eth-pf-v8-reactor-node--commit",
            icon:
              Hash,
            label:
              "COMMIT",
            value:
              "PRE-ROUND"
          },
          {
            className:
              "eth-pf-v8-reactor-node--client",
            icon:
              Fingerprint,
            label:
              "CLIENT SEED",
            value:
              "RECORDED"
          },
          {
            className:
              "eth-pf-v8-reactor-node--nonce",
            icon:
              Binary,
            label:
              "NONCE",
            value:
              "1842"
          },
          {
            className:
              "eth-pf-v8-reactor-node--ruleset",
            icon:
              GitBranch,
            label:
              "RULESET",
            value:
              "v3.2.0"
          },
          {
            className:
              "eth-pf-v8-reactor-node--reveal",
            icon:
              KeyRound,
            label:
              "REVEAL",
            value:
              "MATCH"
          },
          {
            className:
              "eth-pf-v8-reactor-node--replay",
            icon:
              BadgeCheck,
            label:
              "REPLAY",
            value:
              "PASS"
          }
        ].map(
          (
            node,
            index
          ) => {
            const Icon =
              node.icon;

            return (
              <motion.div
                key={
                  node.label
                }
                className={`eth-pf-v8-reactor-node ${node.className}`}
                animate={
                  reduceMotion
                    ? {}
                    : {
                        y: [
                          0,
                          index % 2 === 0
                            ? -8
                            : 8,
                          0
                        ],
                        x: [
                          0,
                          index % 3 === 0
                            ? 4
                            : -4,
                          0
                        ]
                      }
                }
                transition={{
                  duration:
                    4.4 +
                    index *
                    0.24,
                  repeat:
                    Infinity,
                  ease:
                    "easeInOut"
                }}
              >
                <Icon
                  size={15}
                />

                <div>
                  <span>
                    {
                      node.label
                    }
                  </span>

                  <strong>
                    {
                      node.value
                    }
                  </strong>
                </div>
              </motion.div>
            );
          }
        )}


        <span className="eth-pf-v8-packet eth-pf-v8-packet--one" />
        <span className="eth-pf-v8-packet eth-pf-v8-packet--two" />
        <span className="eth-pf-v8-packet eth-pf-v8-packet--three" />
      </div>


      <div className="eth-pf-v8-reactor-result">
        <div>
          <span>
            EXAMPLE REPLAY
          </span>

          <strong>
            WIN / 2.48x
          </strong>
        </div>

        <div className="eth-pf-v8-reactor-result-arrow">
          <ArrowRight
            size={16}
          />
        </div>

        <div>
          <span>
            RECORDED
          </span>

          <strong>
            WIN / 2.48x
          </strong>
        </div>

        <b>
          <Check
            size={13}
          />
          EXAMPLE MATCH
        </b>
      </div>
    </div>
  );
};


/* ============================================================
   PAGE
============================================================ */

const ProvablyFairHelper = () => {
  const reduceMotion =
    useReducedMotion();

  const [
    activeStage,
    setActiveStage
  ] =
    useState(
      0
    );

  const [
    activeArtifact,
    setActiveArtifact
  ] =
    useState(
      0
    );

  const [
    openFaq,
    setOpenFaq
  ] =
    useState(
      0
    );

  const [
    copied,
    setCopied
  ] =
    useState(
      false
    );


  const currentStage =
    verificationStages[
      activeStage
    ];

  const CurrentStageIcon =
    currentStage.icon;

  const currentArtifact =
    proofArtifacts[
      activeArtifact
    ];

  const CurrentArtifactIcon =
    currentArtifact.icon;


  const {
    scrollYProgress
  } =
    useScroll();


  const progress =
    useSpring(
      scrollYProgress,
      {
        stiffness:
          120,
        damping:
          28,
        restDelta:
          0.001
      }
    );


  const heroY =
    useTransform(
      scrollYProgress,
      [
        0,
        0.16
      ],
      [
        0,
        reduceMotion
          ? 0
          : 72
      ]
    );


  const heroOpacity =
    useTransform(
      scrollYProgress,
      [
        0,
        0.13
      ],
      [
        1,
        0.76
      ]
    );


  const tickerItems =
    useMemo(
      () => [
        "GAME-SPECIFIC AUDITS",
        "DECK ORDER CHECKS",
        "NUMERICAL RESULT TRACING",
        "SEEDED RNG VERIFICATION",
        "SKILL EVENT HISTORY",
        "SANDBOX ROUND REPLAY",
        "HISTORICAL RULESETS",
        "ETHICAL GAME DESIGN"
      ],
      []
    );


  const copyProofModel =
    async () => {
      try {
        await navigator.clipboard.writeText(
          "Seeded games: commitment + revealed seed + client seed + nonce + ruleset -> outcome check | Event games: recorded inputs + historical state + ruleset -> sandbox replay"
        );

        setCopied(
          true
        );

        window.setTimeout(
          () =>
            setCopied(
              false
            ),
          1400
        );
      } catch {
        setCopied(
          false
        );
      }
    };


  return (
    <Fragment>

      <motion.div
        className="eth-pf-v8-scroll-progress"
        style={{
          scaleX:
            progress
        }}
      />

      <HeaderToMainPage />


      <main
        className="eth-pf-v8-page"
        id="ethosino-provably-fair-v8"
      >

        {/* ====================================================
            ATMOSPHERE
        ==================================================== */}

        <div
          className="eth-pf-v8-atmosphere"
          aria-hidden="true"
        >
          <div className="eth-pf-v8-atmosphere-grid" />

          <div className="eth-pf-v8-atmosphere-noise" />

          <div className="eth-pf-v8-orb eth-pf-v8-orb--cyan" />

          <div className="eth-pf-v8-orb eth-pf-v8-orb--violet" />

          <div className="eth-pf-v8-orb eth-pf-v8-orb--green" />

          <div className="eth-pf-v8-orb eth-pf-v8-orb--pink" />
        </div>


        {/* ====================================================
            HERO
        ==================================================== */}

        <section
            style={{ marginTop: 50 }}
          className="eth-pf-v8-hero"
          id="pf-overview"
        >

          <motion.div
            className="eth-pf-v8-hero-parallax"
            style={{
              y:
                heroY,
              opacity:
                heroOpacity
            }}
          >

            <div className="eth-pf-v8-shell eth-pf-v8-hero-grid">

              <div className="eth-pf-v8-hero-copy">

                <motion.div
                  className="eth-pf-v8-system-badge"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -35
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.58
                  }}
                >
                  <span className="eth-pf-v8-live-dot" />

                  FAIRNESS AUDITING & GAME ETHICS

                  <i />

                  PER-GAME AUDIT TRAILS
                </motion.div>


                <motion.h1
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -105,
                          y:
                            42,
                          rotate:
                            -2
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0,
                    y:
                      0,
                    rotate:
                      0
                  }}
                  transition={{
                    duration:
                      0.84,
                    delay:
                      0.04,
                    ease: [
                      0.16,
                      1,
                      0.3,
                      1
                    ]
                  }}
                >
                  Fairness goes beyond RNG.

                  <span>
                    Replay what really happened.
                  </span>
                </motion.h1>


                <motion.p
                  className="eth-pf-v8-hero-lead"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            30
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.72,
                    delay:
                      0.14
                  }}
                >
                  Ethosino approaches game integrity according to how each game actually works. Seeded randomness, deck order, numerical results and skill-based actions need different evidence. Each first-party game's sandbox provides a place to replay available historical events and inspect the result outside the live round.
                </motion.p>


                <motion.div
                  className="eth-pf-v8-hero-thesis"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          x:
                            -45
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    x:
                      0
                  }}
                  transition={{
                    duration:
                      0.66,
                    delay:
                      0.24
                  }}
                >
                  <ShieldCheck
                    size={18}
                  />

                  <span>
                    A fair game is more than <strong>“a passing hash.”</strong>
                    Operators and reviewers should be able
                    to ask: <strong>“what evidence explains this game?”</strong>
                  </span>
                </motion.div>


                <motion.div
                  className="eth-pf-v8-hero-actions"
                  initial={
                    reduceMotion
                      ? false
                      : {
                          opacity:
                            0,
                          y:
                            24
                        }
                  }
                  animate={{
                    opacity:
                      1,
                    y:
                      0
                  }}
                  transition={{
                    duration:
                      0.62,
                    delay:
                      0.31
                  }}
                >
                  <a
                    href="#pf-proof-contract"
                    className="eth-pf-v8-btn eth-pf-v8-btn--primary"
                  >
                    How verification works

                    <ArrowRight
                      size={17}
                    />
                  </a>


                  <Link
                    to="/book-demo"
                    className="eth-pf-v8-btn eth-pf-v8-btn--ghost"
                  >
                    Request an audit walkthrough
                  </Link>
                </motion.div>


                <div className="eth-pf-v8-hero-proofline">

                  <span>
                    <Hash
                      size={14}
                    />
                    seed commitments
                  </span>

                  <span>
                    <Binary
                      size={14}
                    />
                    deck shuffles
                  </span>

                  <span>
                    <GitBranch
                      size={14}
                    />
                    numeric outcomes
                  </span>

                  <span>
                    <Layers3
                      size={14}
                    />
                    game-specific
                  </span>

                  <span>
                    <RefreshCw
                      size={14}
                    />
                    sandbox replay
                  </span>

                </div>

              </div>


              <motion.div
                className="eth-pf-v8-hero-visual"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity:
                          0,
                        x:
                          120,
                        y:
                          -60,
                        rotate:
                          2.5,
                        scale:
                          0.94
                      }
                }
                animate={{
                  opacity:
                    1,
                  x:
                    0,
                  y:
                    0,
                  rotate:
                    0,
                  scale:
                    1
                }}
                transition={{
                  duration:
                    0.94,
                  delay:
                    0.12,
                  ease: [
                    0.16,
                    1,
                    0.3,
                    1
                  ]
                }}
              >

                <ProofReactor
                  reduceMotion={
                    reduceMotion
                  }
                />


                <motion.div
                  className="eth-pf-v8-float eth-pf-v8-float--one"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            -12,
                            0
                          ],
                          rotate: [
                            -1,
                            1,
                            -1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      5.5,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <Hash
                    size={15}
                  />

                  <div>
                    <span>
                      COMMITMENT
                    </span>

                    <strong>
                      PRE-ROUND HASH
                    </strong>
                  </div>
                </motion.div>


                <motion.div
                  className="eth-pf-v8-float eth-pf-v8-float--two"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          y: [
                            0,
                            11,
                            0
                          ],
                          rotate: [
                            1,
                            -1,
                            1
                          ]
                        }
                  }
                  transition={{
                    duration:
                      6.2,
                    repeat:
                      Infinity,
                    ease:
                      "easeInOut"
                  }}
                >
                  <BadgeCheck
                    size={15}
                  />

                  <div>
                    <span>
                      ROUND REPLAY
                    </span>

                    <strong>
                      EXAMPLE MATCH
                    </strong>
                  </div>
                </motion.div>

              </motion.div>

            </div>

          </motion.div>


          <div className="eth-pf-v8-ticker">
            <div className="eth-pf-v8-ticker-track">
              {
                [
                  ...tickerItems,
                  ...tickerItems
                ].map(
                  (
                    item,
                    index
                  ) => (
                    <React.Fragment
                      key={`${item}-${index}`}
                    >
                      <span>
                        {
                          item
                        }
                      </span>

                      <i>
                        ◆
                      </i>
                    </React.Fragment>
                  )
                )
              }
            </div>
          </div>

        </section>


        {/* ====================================================
            SUBNAV
        ==================================================== */}

        <nav className="eth-pf-v8-subnav">
          <div className="eth-pf-v8-shell eth-pf-v8-subnav-inner">

            <div className="eth-pf-v8-subnav-brand">
              <span />
              AUDIT & ETHICS
            </div>


            <div className="eth-pf-v8-subnav-links">

              <a href="#pf-why">
                Context
              </a>

              <a href="#pf-proof-contract">
                Method
              </a>

              <a href="#pf-artifacts">
                Evidence
              </a>

              <a href="#pf-lifecycle">
                Controls
              </a>

              <a href="#pf-boundaries">
                Limits
              </a>

              <a href="#pf-faq">
                FAQ
              </a>

              <Link
                to="/book-demo"
                className="eth-pf-v8-subnav-cta"
              >
                Demo
              </Link>

            </div>

          </div>
        </nav>


        {/* ====================================================
            POV / TRUST DEBT
        ==================================================== */}

        <section
          className="eth-pf-v8-section"
          id="pf-why"
        >
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="topLeft"
              className="eth-pf-v8-section-head eth-pf-v8-section-head--split"
            >
              <div>
                <span className="eth-pf-v8-kicker">
                  WHY GAME-SPECIFIC AUDITS MATTER
                </span>

                <h2>
                  Different games create{" "}
                  <span>
                    different audit questions.
                  </span>
                </h2>
              </div>

              <p>
                A shuffled deck, a numerical draw and a skill-based match do not produce evidence in the same way. An operator needs to understand the applicable game rules, inputs and event history before they can meaningfully assess a complaint. Ethosino is designed to make that examination possible instead of relying on one universal fairness label.
              </p>
            </Reveal>


            <div className="eth-pf-v8-trust-grid">

              <Reveal
                direction="bottomLeft"
              >
                <Spotlight className="eth-pf-v8-trust-card eth-pf-v8-trust-card--legacy">

                  <div className="eth-pf-v8-trust-card-top">
                    <span>
                      01
                    </span>

                    <LockKeyhole
                      size={21}
                    />
                  </div>

                  <small>
                    OUTCOME-ONLY RECORD
                  </small>

                  <h3>
                    “The result was already settled.”
                  </h3>

                  <p>
                    A final balance and win/loss status may establish what was paid, but not why a card was drawn, a number selected or a skill-game action accepted. Without the intermediate record, important questions remain unanswered.
                  </p>

                  <div className="eth-pf-v8-trust-chain eth-pf-v8-trust-chain--muted">
                    <span>
                      RESULT
                    </span>

                    <ArrowRight
                      size={14}
                    />

                    <span>
                      SUPPORT CLAIM
                    </span>

                    <ArrowRight
                      size={14}
                    />

                    <span>
                      UNCERTAINTY
                    </span>
                  </div>

                </Spotlight>
              </Reveal>


              <Reveal
                direction="bottomRight"
              >
                <Spotlight className="eth-pf-v8-trust-card eth-pf-v8-trust-card--proof">

                  <div className="eth-pf-v8-trust-card-top">
                    <span>
                      02
                    </span>

                    <Fingerprint
                      size={21}
                    />
                  </div>

                  <small>
                    GAME-AWARE AUDIT RECORD
                  </small>

                  <h3>
                    “Open the round. Follow the events.”
                  </h3>

                  <p>
                    A game-specific sandbox restores the available historical context: the rules in force, applicable random inputs, event sequence and resulting state. Auditors can distinguish a cryptographic check from a behavioral or rules-based replay.
                  </p>

                  <div className="eth-pf-v8-trust-chain">
                    <span>
                      INPUTS
                    </span>

                    <ArrowRight
                      size={14}
                    />

                    <span>
                      EVENTS
                    </span>

                    <ArrowRight
                      size={14}
                    />

                    <span>
                      COMPARE
                    </span>
                  </div>

                </Spotlight>
              </Reveal>

            </div>


            <Reveal
              direction="right"
              className="eth-pf-v8-manifesto"
            >
              <div className="eth-pf-v8-manifesto-icon">
                <Eye
                  size={20}
                />
              </div>

              <div>
                <span>
                  DESIGNED FOR ACCOUNTABILITY
                </span>

                <h3>
                  Different methods. The same standard:{" "}
                  <strong>
                    an outcome that can be questioned and examined.
                  </strong>
                </h3>
              </div>
            </Reveal>

          </div>
        </section>


        {/* ====================================================
            INTERACTIVE PROOF CONTRACT
        ==================================================== */}

        <section
          className="eth-pf-v8-section eth-pf-v8-section--panel"
          id="pf-proof-contract"
        >
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="topRight"
              className="eth-pf-v8-section-head"
            >
              <span className="eth-pf-v8-kicker">
                SEEDED OUTCOME VERIFICATION
              </span>

              <h2>
                For supported random games,{" "}
                <span>
                  the inputs can be checked.
                </span>
              </h2>

              <p>
                This four-stage model concerns outcomes derived from Ethosino-controlled seed material. Deck, numeric and other RNG mappings can differ by game; skill inputs and stateful events require the separate sandbox audit path shown below.
              </p>
            </Reveal>


            <div className="eth-pf-v8-contract">

              <Reveal
                direction="left"
                className="eth-pf-v8-contract-tabs"
              >

                {
                  verificationStages.map(
                    (
                      stage,
                      index
                    ) => {
                      const Icon =
                        stage.icon;

                      const active =
                        activeStage ===
                        index;

                      return (
                        <motion.button
                          type="button"
                          key={
                            stage.id
                          }
                          className={[
                            "eth-pf-v8-contract-tab",
                            `eth-pf-v8-contract-tab--${stage.accent}`,
                            active
                              ? "is-active"
                              : ""
                          ].join(
                            " "
                          )}
                          onClick={() =>
                            setActiveStage(
                              index
                            )
                          }
                          whileHover={{
                            x:
                              5
                          }}
                          whileTap={{
                            scale:
                              0.985
                          }}
                        >
                          <span className="eth-pf-v8-contract-tab-index">
                            {
                              stage.index
                            }
                          </span>

                          <span className="eth-pf-v8-contract-tab-icon">
                            <Icon
                              size={18}
                            />
                          </span>

                          <span className="eth-pf-v8-contract-tab-copy">
                            <small>
                              {
                                stage.kicker
                              }
                            </small>

                            <strong>
                              {
                                stage.title
                              }
                            </strong>

                            <em>
                              {
                                stage.short ||
                                stage.headline
                              }
                            </em>
                          </span>

                          <ArrowRight
                            size={15}
                          />
                        </motion.button>
                      );
                    }
                  )
                }

              </Reveal>


              <Reveal
                direction="right"
                className="eth-pf-v8-contract-detail-wrap"
              >

                <AnimatePresence
                  mode="wait"
                >
                  <motion.article
                    key={
                      currentStage.id
                    }
                    className={`eth-pf-v8-contract-detail eth-pf-v8-contract-detail--${currentStage.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity:
                              0,
                            x:
                              76,
                            y:
                              -26,
                            rotate:
                              1.3,
                            filter:
                              "blur(8px)"
                          }
                    }
                    animate={{
                      opacity:
                        1,
                      x:
                        0,
                      y:
                        0,
                      rotate:
                        0,
                      filter:
                        "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity:
                              0,
                            x:
                              -60,
                            y:
                              24,
                            rotate:
                              -1.2,
                            filter:
                              "blur(7px)"
                          }
                    }
                    transition={{
                      duration:
                        0.34
                    }}
                  >

                    <div className="eth-pf-v8-contract-detail-top">
                      <span className="eth-pf-v8-contract-big-index">
                        {
                          currentStage.index
                        }
                      </span>

                      <div className="eth-pf-v8-contract-detail-icon">
                        <CurrentStageIcon
                          size={28}
                        />
                      </div>
                    </div>


                    <small>
                      {
                        currentStage.kicker
                      }
                    </small>

                    <h3>
                      {
                        currentStage.headline
                      }
                    </h3>

                    <p>
                      {
                        currentStage.body
                      }
                    </p>


                    <div className="eth-pf-v8-contract-telemetry">
                      {
                        currentStage.telemetry.map(
                          (
                            row
                          ) => (
                            <div
                              key={
                                row[0]
                              }
                            >
                              <span>
                                {
                                  row[0]
                                }
                              </span>

                              <strong>
                                {
                                  row[1]
                                }
                              </strong>
                            </div>
                          )
                        )
                      }
                    </div>


                    <div className="eth-pf-v8-contract-evidence">
                      {
                        currentStage.evidence.map(
                          (
                            item
                          ) => (
                            <div
                              key={
                                item
                              }
                            >
                              <span>
                                <Check
                                  size={12}
                                  strokeWidth={3}
                                />
                              </span>

                              {
                                item
                              }
                            </div>
                          )
                        )
                      }
                    </div>


                    <div className="eth-pf-v8-contract-flow">
                      <span>
                        COMMITMENT
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        ROUND
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        DISCLOSURE
                      </span>

                      <ArrowRight
                        size={14}
                      />

                      <span>
                        REPLAY
                      </span>
                    </div>

                  </motion.article>
                </AnimatePresence>

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            ARTIFACT EXPLORER
        ==================================================== */}

        <section
          className="eth-pf-v8-section"
          id="pf-artifacts"
        >
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-pf-v8-section-head eth-pf-v8-section-head--split"
            >
              <div>
                <span className="eth-pf-v8-kicker">
                  INSIDE AN AUDIT RECORD
                </span>

                <h2>
                  An auditor needs more than{" "}
                  <span>
                    the final result.
                  </span>
                </h2>
              </div>

              <p>
                A seed hash is meaningful only alongside the game, round and applicable history. This explorer shows example fields from a seeded-round evidence record; event-driven and skill games additionally depend on recorded actions and state changes.
              </p>
            </Reveal>


            <div className="eth-pf-v8-artifact-layout">

              <Reveal
                direction="topLeft"
                className="eth-pf-v8-artifact-map"
              >

                <div className="eth-pf-v8-artifact-map-grid" />

                <div className="eth-pf-v8-artifact-map-core">
                  <FileCheck2
                    size={25}
                  />

                  <span>
                    ROUND
                  </span>

                  <strong>
                    AUDIT INPUTS
                  </strong>
                </div>


                {
                  proofArtifacts.map(
                    (
                      artifact,
                      index
                    ) => {
                      const Icon =
                        artifact.icon;

                      return (
                        <motion.button
                          type="button"
                          key={
                            artifact.id
                          }
                          className={[
                            "eth-pf-v8-artifact-node",
                            `eth-pf-v8-artifact-node--${index + 1}`,
                            activeArtifact === index
                              ? "is-active"
                              : ""
                          ].join(
                            " "
                          )}
                          onClick={() =>
                            setActiveArtifact(
                              index
                            )
                          }
                          animate={
                            reduceMotion
                              ? {}
                              : {
                                  y: [
                                    0,
                                    index % 2 === 0
                                      ? -7
                                      : 7,
                                    0
                                  ]
                                }
                          }
                          transition={{
                            duration:
                              4 +
                              index *
                              0.22,
                            repeat:
                              Infinity,
                            ease:
                              "easeInOut"
                          }}
                        >
                          <Icon
                            size={16}
                          />

                          <div>
                            <small>
                              {
                                artifact.label
                              }
                            </small>

                            <strong>
                              {
                                artifact.value
                              }
                            </strong>
                          </div>
                        </motion.button>
                      );
                    }
                  )
                }


                <span className="eth-pf-v8-artifact-packet eth-pf-v8-artifact-packet--one" />

                <span className="eth-pf-v8-artifact-packet eth-pf-v8-artifact-packet--two" />

              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-pf-v8-artifact-detail-wrap"
              >
                <AnimatePresence
                  mode="wait"
                >
                  <motion.article
                    key={
                      currentArtifact.id
                    }
                    className="eth-pf-v8-artifact-detail"
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity:
                              0,
                            x:
                              46,
                            y:
                              28,
                            filter:
                              "blur(7px)"
                          }
                    }
                    animate={{
                      opacity:
                        1,
                      x:
                        0,
                      y:
                        0,
                      filter:
                        "blur(0px)"
                    }}
                    exit={
                      reduceMotion
                        ? undefined
                        : {
                            opacity:
                              0,
                            x:
                              -36,
                            y:
                              -20,
                            filter:
                              "blur(6px)"
                          }
                    }
                    transition={{
                      duration:
                        0.31
                    }}
                  >

                    <div className="eth-pf-v8-artifact-detail-head">
                      <div className="eth-pf-v8-artifact-detail-icon">
                        <CurrentArtifactIcon
                          size={24}
                        />
                      </div>

                      <div>
                        <span>
                          SELECTED EVIDENCE
                        </span>

                        <strong>
                          {
                            currentArtifact.label
                          }
                        </strong>
                      </div>
                    </div>


                    <h3>
                      {
                        currentArtifact.title
                      }
                    </h3>

                    <p>
                      {
                        currentArtifact.description
                      }
                    </p>


                    <div className="eth-pf-v8-artifact-value">
                      <span>
                        ILLUSTRATIVE VALUE
                      </span>

                      <strong>
                        {
                          currentArtifact.value
                        }
                      </strong>
                    </div>


                    <div className="eth-pf-v8-artifact-principle">
                      <Link2
                        size={17}
                      />

                      <div>
                        <span>
                          WHY IT MATTERS
                        </span>

                        <p>
                          Use the record to identify which round, rules and inputs are being checked. Example values on this page illustrate the data model and should not be mistaken for a live audit.
                        </p>
                      </div>
                    </div>


                    <button
                      type="button"
                      className="eth-pf-v8-copy-button"
                      onClick={
                        copyProofModel
                      }
                    >
                      <Copy
                        size={14}
                      />

                      {
                        copied
                          ? "Copied audit model"
                          : "Copy audit model"
                      }
                    </button>

                  </motion.article>
                </AnimatePresence>
              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            REPLAY ROOM
        ==================================================== */}

        <section className="eth-pf-v8-section eth-pf-v8-section--panel">
          <div className="eth-pf-v8-shell">

            <div className="eth-pf-v8-replay-layout">

              <Reveal
                direction="left"
                className="eth-pf-v8-replay-copy"
              >
                <span className="eth-pf-v8-kicker">
                  PER-GAME REPLAY SANDBOX
                </span>

                <h2>
                  Reopen historical gameplay in{" "}
                  <span>
                    an isolated review environment.
                  </span>
                </h2>

                <p>
                  Each first-party game has its own sandbox environment for replaying available previous-game events. The reviewer can inspect the recorded action sequence, relevant random inputs and resulting state using the historical game rules. A sandbox reconstruction is an audit exercise, not another live wager or a retroactive change to settlement.
                </p>


                <div className="eth-pf-v8-replay-points">

                  <div>
                    <span>
                      01
                    </span>

                    <p>
                      <strong>
                        Support
                      </strong>{" "}
                      can examine the precise step a player disputes.
                    </p>
                  </div>

                  <div>
                    <span>
                      02
                    </span>

                    <p>
                      <strong>
                        Operators
                      </strong>{" "}
                      can compare saved game events with a sandbox reconstruction.
                    </p>
                  </div>

                  <div>
                    <span>
                      03
                    </span>

                    <p>
                      <strong>
                        Audit reviewers
                      </strong>{" "}
                      can distinguish random-result verification from a skill or state replay.
                    </p>
                  </div>

                </div>


                <Link
                  to="/book-demo"
                  className="eth-pf-v8-inline-link"
                >
                  Request a sandbox walkthrough

                  <ArrowRight
                    size={15}
                  />
                </Link>
              </Reveal>


              <Reveal
                direction="topRight"
                className="eth-pf-v8-terminal"
              >
                <div className="eth-pf-v8-terminal-beam" />

                <header>
                  <div>
                    <span />
                    <span />
                    <span />
                  </div>

                  <small>
                    ethosino / sandbox / example
                  </small>
                </header>


                <div className="eth-pf-v8-terminal-body">

                  <p>
                    <span>
                      $
                    </span>{" "}
                    replay-round --example rnd_9c3e8a1f
                  </p>


                  {
                    [
                      [
                        "tenant",
                        "example-tenant",
                        ""
                      ],
                      [
                        "ruleset",
                        "mines:v3.2.0",
                        ""
                      ],
                      [
                        "seed check",
                        "EXAMPLE MATCH",
                        "pass"
                      ],
                      [
                        "input seed",
                        "player-session-08f1",
                        ""
                      ],
                      [
                        "nonce",
                        "1842",
                        ""
                      ],
                      [
                        "sandbox outcome",
                        "WIN / 2.48x",
                        ""
                      ],
                      [
                        "stored outcome",
                        "WIN / 2.48x",
                        ""
                      ],
                      [
                        "event reference",
                        "RECORDED",
                        "pass"
                      ]
                    ].map(
                      (
                        row
                      ) => (
                        <div
                          className="eth-pf-v8-terminal-row"
                          key={
                            row[0]
                          }
                        >
                          <span>
                            {
                              row[0]
                            }
                          </span>

                          <strong
                            className={
                              row[2] === "pass"
                                ? "is-pass"
                                : ""
                            }
                          >
                            {
                              row[1]
                            }
                          </strong>
                        </div>
                      )
                    )
                  }


                  <div className="eth-pf-v8-terminal-result">
                    <ShieldCheck
                      size={20}
                    />

                    <div>
                      <span>
                        EXAMPLE AUDIT RESULT
                      </span>

                      <strong>
                        ILLUSTRATIVE REPLAY — NOT A LIVE AUDIT
                      </strong>
                    </div>
                  </div>

                </div>
              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            LIFECYCLE
        ==================================================== */}

        <section
          className="eth-pf-v8-section"
          id="pf-lifecycle"
        >
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="topLeft"
              className="eth-pf-v8-section-head"
            >
              <span className="eth-pf-v8-kicker">
                ONGOING INTEGRITY CONTROLS
              </span>

              <h2>
                Auditability must survive{" "}
                <span>
                  every game update.
                </span>
              </h2>

              <p>
                A trustworthy historical replay depends on work done before and after the round: defining valid mechanics, versioning changes, capturing events, protecting sensitive inputs, isolating replays and reviewing discrepancies. These practices apply across more than one randomness model.
              </p>
            </Reveal>


            <div className="eth-pf-v8-lifecycle">

              <div
                className="eth-pf-v8-lifecycle-line"
                aria-hidden="true"
              >
                <span />
              </div>


              {
                lifecycleSteps.map(
                  (
                    step,
                    index
                  ) => {
                    const Icon =
                      step.icon;

                    const directions = [
                      "topLeft",
                      "topRight",
                      "left",
                      "right",
                      "bottomLeft",
                      "bottomRight"
                    ];

                    return (
                      <Reveal
                        key={
                          step.index
                        }
                        direction={
                          directions[
                            index
                          ]
                        }
                        delay={
                          index *
                          0.035
                        }
                      >
                        <motion.article
                          className="eth-pf-v8-lifecycle-card"
                          whileHover={
                            reduceMotion
                              ? undefined
                              : {
                                  y:
                                    -6,
                                  rotateX:
                                    1.5,
                                  rotateY:
                                    index %
                                    2 ===
                                    0
                                      ? -1.5
                                      : 1.5
                                }
                          }
                        >
                          <span className="eth-pf-v8-lifecycle-index">
                            {
                              step.index
                            }
                          </span>

                          <div className="eth-pf-v8-lifecycle-icon">
                            <Icon
                              size={20}
                            />
                          </div>

                          <h3>
                            {
                              step.title
                            }
                          </h3>

                          <p>
                            {
                              step.body
                            }
                          </p>
                        </motion.article>
                      </Reveal>
                    );
                  }
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            ONE ROUND / ONE EVIDENCE PATH
        ==================================================== */}

        <section className="eth-pf-v8-section eth-pf-v8-section--panel">
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="bottomRight"
              className="eth-pf-v8-system-story"
            >

              <div className="eth-pf-v8-system-story-copy">
                <span className="eth-pf-v8-kicker">
                  COMPLETE ROUND ACCOUNTABILITY
                </span>

                <h2>
                  The audit follows the round{" "}
                  <span>
                    beyond the game screen.
                  </span>
                </h2>

                <p>
                  Game fairness and settlement accuracy are related, but separate questions. A reviewer should be able to follow a historical game event to the recorded account movement and any administrative review, without treating a cryptographic hash as proof of the entire financial workflow.
                </p>
              </div>


              <div className="eth-pf-v8-system-path">

                <div className="eth-pf-v8-system-track" />

                {
                  [
                    {
                      icon:
                        Fingerprint,
                      label:
                        "FAIRNESS",
                      value:
                        "game-specific check"
                    },
                    {
                      icon:
                        Gamepad2,
                      label:
                        "GAME HISTORY",
                      value:
                        "inputs + actions"
                    },
                    {
                      icon:
                        Database,
                      label:
                        "SETTLEMENT",
                      value:
                        "balance record"
                    },
                    {
                      icon:
                        FileCheck2,
                      label:
                        "AUDIT",
                      value:
                        "recorded decisions"
                    }
                  ].map(
                    (
                      item,
                      index
                    ) => {
                      const Icon =
                        item.icon;

                      return (
                        <motion.div
                          className="eth-pf-v8-system-node"
                          key={
                            item.label
                          }
                          initial={
                            reduceMotion
                              ? false
                              : {
                                  opacity:
                                    0,
                                  y:
                                    35
                                }
                          }
                          whileInView={{
                            opacity:
                              1,
                            y:
                              0
                          }}
                          viewport={{
                            once:
                              true
                          }}
                          transition={{
                            delay:
                              index *
                              0.08
                          }}
                        >
                          <div>
                            <Icon
                              size={19}
                            />
                          </div>

                          <span>
                            {
                              item.label
                            }
                          </span>

                          <strong>
                            {
                              item.value
                            }
                          </strong>
                        </motion.div>
                      );
                    }
                  )
                }


                <motion.span
                  className="eth-pf-v8-system-packet"
                  animate={
                    reduceMotion
                      ? {}
                      : {
                          left: [
                            "2%",
                            "95%"
                          ]
                        }
                  }
                  transition={{
                    duration:
                      5.4,
                    repeat:
                      Infinity,
                    ease:
                      "linear"
                  }}
                />

              </div>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            BOUNDARIES
        ==================================================== */}

        <section
          className="eth-pf-v8-section"
          id="pf-boundaries"
        >
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="topRight"
              className="eth-pf-v8-section-head"
            >
              <span className="eth-pf-v8-kicker">
                THE LIMITS OF VERIFICATION
              </span>

              <h2>
                Precision makes an audit{" "}
                <span>
                  worth trusting.
                </span>
              </h2>

              <p>
                A matched seed commitment, reconstructed deck or consistent event replay supports specific conclusions. It does not certify payout design, eliminate every vulnerability, or approve a business to operate. We separate what an audit can substantiate from what still requires people, policy and independent oversight.
              </p>
            </Reveal>


            <div className="eth-pf-v8-boundary-grid">

              {
                boundaryCards.map(
                  (
                    card,
                    index
                  ) => {
                    const Icon =
                      card.icon;

                    return (
                      <Reveal
                        key={
                          card.type
                        }
                        direction={
                          index === 0
                            ? "bottomLeft"
                            : "bottomRight"
                        }
                      >
                        <Spotlight
                          className={`eth-pf-v8-boundary-card eth-pf-v8-boundary-card--${card.type}`}
                        >
                          <div className="eth-pf-v8-boundary-head">
                            <span>
                              <Icon
                                size={18}
                              />
                            </span>

                            <div>
                              <small>
                                {
                                  card.eyebrow
                                }
                              </small>

                              <h3>
                                {
                                  card.title
                                }
                              </h3>
                            </div>
                          </div>


                          <div className="eth-pf-v8-boundary-list">
                            {
                              card.items.map(
                                (
                                  item
                                ) => (
                                  <div
                                    key={
                                      item
                                    }
                                  >
                                    <span>
                                      <Icon
                                        size={12}
                                      />
                                    </span>

                                    <p>
                                      {
                                        item
                                      }
                                    </p>
                                  </div>
                                )
                              )
                            }
                          </div>
                        </Spotlight>
                      </Reveal>
                    );
                  }
                )
              }

            </div>


            <Reveal
              direction="left"
              className="eth-pf-v8-third-party-note"
            >
              <TriangleAlert
                size={18}
              />

              <div>
                <span>
                  PROVIDER-CONTROLLED OUTCOMES
                </span>

                <p>
                  Ethosino can describe and audit the first-party mechanisms it operates. External game providers control their own random systems, certification and evidence; those outcomes should not be represented as verified by Ethosino’s first-party methods.
                </p>
              </div>
            </Reveal>

          </div>
        </section>


        {/* ====================================================
            FAIRNESS METHODS BY GAME TYPE
        ==================================================== */}

        <section className="eth-pf-v8-section eth-pf-v8-section--panel">
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="topLeft"
              className="eth-pf-v8-section-head eth-pf-v8-section-head--split"
            >
              <div>
                <span className="eth-pf-v8-kicker">
                  OPERATOR VALUE
                </span>

                <h2>
                  One audit method cannot fit{" "}
                  <span>
                    every kind of game.
                  </span>
                </h2>
              </div>

              <p>
                Random draws, shuffled decks and player-controlled actions leave different kinds of evidence. Ethosino’s first-party design takes those differences seriously, applying the appropriate verification or historical replay method to the specific mechanics involved.
              </p>
            </Reveal>


            <div className="eth-pf-v8-value-grid">

              {
                operatorOutcomes.map(
                  (
                    outcome,
                    index
                  ) => {
                    const Icon =
                      outcome.icon;

                    const directions = [
                      "topLeft",
                      "topRight",
                      "bottomLeft",
                      "bottomRight"
                    ];

                    return (
                      <Reveal
                        key={
                          outcome.title
                        }
                        direction={
                          directions[
                            index
                          ]
                        }
                        delay={
                          index *
                          0.05
                        }
                      >
                        <Spotlight className="eth-pf-v8-value-card">

                          <div className="eth-pf-v8-value-card-top">
                            <span>
                              0{
                                index +
                                1
                              }
                            </span>

                            <Icon
                              size={20}
                            />
                          </div>

                          <small>
                            {
                              outcome.label
                            }
                          </small>

                          <h3>
                            {
                              outcome.title
                            }
                          </h3>

                          <p>
                            {
                              outcome.body
                            }
                          </p>

                        </Spotlight>
                      </Reveal>
                    );
                  }
                )
              }

            </div>

          </div>
        </section>


        {/* ====================================================
            PRINCIPLE
        ==================================================== */}

        <section className="eth-pf-v8-principle">
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="right"
              className="eth-pf-v8-principle-inner"
            >

              <div className="eth-pf-v8-principle-icon">
                <Workflow
                  size={25}
                />
              </div>


              <div>
                <span>
                  ETHOSINO / ETHICAL GAME ENGINEERING
                </span>

                <h2>
                  Fairness is also about{" "}
                  <strong>
                    honest rules, player protections and a real path to challenge results.
                  </strong>
                </h2>
              </div>


              <Link
                to="/book-demo"
                className="eth-pf-v8-principle-cta"
              >
                Discuss audit requirements

                <ArrowRight
                  size={16}
                />
              </Link>

            </Reveal>

          </div>
        </section>


        {/* ====================================================
            FAQ
        ==================================================== */}

        <section
          className="eth-pf-v8-section"
          id="pf-faq"
        >
          <div className="eth-pf-v8-shell">

            <div className="eth-pf-v8-faq-layout">

              <Reveal
                direction="topLeft"
                className="eth-pf-v8-faq-copy"
              >
                <span className="eth-pf-v8-kicker">
                  TECHNICAL & ETHICAL QUESTIONS
                </span>

                <h2>
                  Questions worth asking{" "}
                  <span>
                    before trusting a result.
                  </span>
                </h2>

                <p>
                  We distinguish cryptographic randomness checks, game-state reconstruction, skill-game evidence and operator responsibilities. These are different checks, and the answers should stay different too.
                </p>


                <div className="eth-pf-v8-faq-callout">
                  <ScanSearch
                    size={20}
                  />

                  <div>
                    <span>
                      WHAT TO ASK US TO SHOW
                    </span>

                    <strong>
                      Choose a game type and walk through its historical audit trail.
                    </strong>
                  </div>
                </div>
              </Reveal>


              <Reveal
                direction="bottomRight"
                className="eth-pf-v8-faq-list"
              >

                {
                  faqItems.map(
                    (
                      item,
                      index
                    ) => {
                      const open =
                        openFaq ===
                        index;

                      return (
                        <article
                          className={[
                            "eth-pf-v8-faq-item",
                            open
                              ? "is-open"
                              : ""
                          ].join(
                            " "
                          )}
                          key={
                            item.q
                          }
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setOpenFaq(
                                open
                                  ? -1
                                  : index
                              )
                            }
                            aria-expanded={
                              open
                            }
                          >
                            <span>
                              {
                                String(
                                  index +
                                  1
                                ).padStart(
                                  2,
                                  "0"
                                )
                              }
                            </span>

                            <strong>
                              {
                                item.q
                              }
                            </strong>

                            <motion.div
                              animate={{
                                rotate:
                                  open
                                    ? 180
                                    : 0
                              }}
                            >
                              <ChevronDown
                                size={17}
                              />
                            </motion.div>
                          </button>


                          <AnimatePresence
                            initial={false}
                          >
                            {
                              open
                                ? (
                                  <motion.div
                                    className="eth-pf-v8-faq-answer"
                                    initial={{
                                      height:
                                        0,
                                      opacity:
                                        0
                                    }}
                                    animate={{
                                      height:
                                        "auto",
                                      opacity:
                                        1
                                    }}
                                    exit={{
                                      height:
                                        0,
                                      opacity:
                                        0
                                    }}
                                    transition={{
                                      duration:
                                        0.28
                                    }}
                                  >
                                    <p>
                                      {
                                        item.a
                                      }
                                    </p>
                                  </motion.div>
                                )
                                : null
                            }
                          </AnimatePresence>
                        </article>
                      );
                    }
                  )
                }

              </Reveal>

            </div>

          </div>
        </section>


        {/* ====================================================
            FINAL
        ==================================================== */}

        <section className="eth-pf-v8-final">
          <div className="eth-pf-v8-shell">

            <Reveal
              direction="bottomLeft"
              className="eth-pf-v8-final-card"
            >

              <div className="eth-pf-v8-final-grid" />

              <div className="eth-pf-v8-final-beam" />


              <div className="eth-pf-v8-final-copy">

                <span>
                  ENGINEERED FOR SCRUTINY
                </span>

                <h2>
                  Make game integrity{" "}
                  <strong>
                    part of the product, not the sales pitch.
                  </strong>
                </h2>

                <p>
                  With first-party game-specific verification methods and replay sandboxes for previous events, Ethosino gives operators a more useful starting point for investigating disputes, reviewing rule changes and documenting game integrity. We welcome technical questions about what each method does—and what it cannot prove.
                </p>


                <div className="eth-pf-v8-final-actions">

                  <Link
                    to="/book-demo"
                    className="eth-pf-v8-btn eth-pf-v8-btn--primary eth-pf-v8-btn--large"
                  >
                    Book a fairness walkthrough

                    <ArrowRight
                      size={18}
                    />
                  </Link>


                  <Link
                    to="/solutions/startup-operators"
                    className="eth-pf-v8-btn eth-pf-v8-btn--ghost eth-pf-v8-btn--large"
                  >
                    Explore the full platform
                  </Link>

                </div>

              </div>


              <div className="eth-pf-v8-final-proof">

                <div className="eth-pf-v8-final-proof-head">
                  <ShieldCheck
                    size={21}
                  />

                  <div>
                    <span>
                      FAIRNESS REVIEW STANDARD
                    </span>

                    <strong>
                      Use the evidence each game requires.
                    </strong>
                  </div>
                </div>


                <div className="eth-pf-v8-final-proof-list">

                  <span>
                    <Check
                      size={13}
                    />
                    Preserve supported random commitments
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Record game actions and inputs
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Protect and rotate secrets
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Replay in game-specific sandboxes
                  </span>

                  <span>
                    <Check
                      size={13}
                    />
                    Document the reviewer’s findings
                  </span>

                </div>

              </div>

            </Reveal>

          </div>
        </section>

      </main>


      <Footer />

    </Fragment>
  );
};


export default ProvablyFairHelper;
